/**
 * Document search results — canon R8's primary surface, on the Hub's front door.
 *
 * ## Why this exists
 *
 * R8: _"the interface's primary surface is GETTING current documents — search-to-open of any
 * accessible document's current version in three interactions or fewer."_ The Hub could not do it.
 * `GET /projects?q=` searches project IDENTITY, and the document list is per-project, so reaching a
 * file required already knowing which project held it. On the X drive you type a filename and get
 * the file. R9 calls a daily verb that loses to the old way a **defect**, "because that verb is
 * where truth leaks back to laptops".
 *
 * Measured against R8 with this in place: **type (1) → click the filename (2)**. Two interactions,
 * and the click is the attributable retrieval route, so the open is recorded like any other.
 *
 * ## ⚠️ R8's SECOND sentence, which is the one that gets dropped
 *
 * _"Every retrieved artifact visibly declares its currency ("current as of" / version) — the
 * never-work-from-a-superseded-drawing guarantee is the product's core promise, **displayed, not
 * implied**."_
 *
 * So the version is rendered on **every** row here, including v1. `ProjectDocuments` suppresses v1
 * as noise and that is defensible in a per-project list a reader is already oriented inside — but
 * this surface returns files from across the whole tenant, with no such context, and "displayed,
 * not implied" is a requirement rather than a preference. The two panels differ on purpose.
 *
 * ## ⚠️ A WITHDRAWN CURRENT VERSION IS SHOWN AS WITHDRAWN, NEVER SUBTRACTED
 *
 * "Current" here means the newest stamp, whatever its withdrawal state — the same definition
 * `/projects/:code/documents` uses, and the only one the substrate can honour: the seam's download
 * is DOCUMENT-addressed and Nexus serves the newest file row, so a panel that displayed an earlier
 * version would hand over the withdrawn one on click. Hiding the row instead would be deletion by
 * another name, which canon forbids. So it is displayed, and labelled.
 */

import type { DocumentHit } from '../api.js';
import { documentDownloadUrl, documentShareUrl } from '../retrieval.js';
import { CopyLinkButton } from './CopyLinkButton.js';
import { timestamp } from '../format.js';

export interface DocumentHitsProps {
  /** The hits, or `'error'` when the find could not be asked. Never conflate the two. */
  result: { documents: DocumentHit[]; truncated: boolean } | 'error' | null;
  /**
   * The term `result` belongs to, and the term in the box. When they differ the answer on screen is
   * about a question the reader has already stopped asking, so no empty state is stated.
   */
  term: string;
  resultTerm: string;
  /**
   * May this caller take a document OUT? From `roles.ts`, the same answer the documents panel uses.
   * A viewer still SEES that the document exists and where it lives — they are simply not handed
   * the file, which is the honest rendering of "you may read that this exists".
   */
  canRetrieve: boolean;
  /** Open a document through the attributable-retrieval path (session renewal, then the download). */
  onRetrieve: (projectRef: string, documentId: string) => void;
  /**
   * The Hub's own origin, for the share link. Passed in rather than read from `window` so this
   * component stays renderable on the server and testable without a DOM.
   */
  origin: string;
}

export function DocumentHits({
  result,
  term,
  resultTerm,
  canRetrieve,
  onRetrieve,
  origin,
}: DocumentHitsProps) {
  const searching = term.trim().length >= 2;
  // Nothing to say before a search has been made. An empty "no documents" panel on the default
  // listing would report an absence nobody asked about.
  if (!searching) return null;

  /*
    Does the answer on screen belong to the term in the box?

    ⚠️ THIS GUARDS THE ERROR BRANCH TOO, and the first version did not — it returned "unavailable"
    before this was consulted, so one failed find painted that message over every later keystroke
    until a non-error response landed. A retry that is in flight then reads as the search being
    down. Same defect as a stale empty state, in the branch added to avoid stale empty states.
    Found by Bugbot.
  */
  const settled = result !== null && resultTerm.trim() === term.trim();

  if (settled && result === 'error') {
    return (
      <section className="dochits" aria-labelledby="dochits-heading">
        <h2 className="dochits__heading" id="dochits-heading">
          Documents
        </h2>
        {/* ⚠️ NOT "no documents match". A failed request is not evidence about the tenant's records,
            and stating one as the other is how a reader concludes a file is missing when the server
            simply did not answer. */}
        <p className="dochits__empty">
          Document search is unavailable right now. Try again shortly.
        </p>
      </section>
    );
  }

  return (
    <section className="dochits" aria-labelledby="dochits-heading">
      <h2 className="dochits__heading" id="dochits-heading">
        Documents
      </h2>
      {!settled || result === 'error' ? (
        // Not settled (including an unsettled error) — the honest state is "still asking".
        <p className="dochits__empty">Searching…</p>
      ) : result.documents.length === 0 ? (
        /*
          ⚠️ A SEARCH THAT FOUND NO DOCUMENT IS NOT AN EMPTY RECORD, and the wording has to carry
          that. "No documents" would tell someone with four hundred files that they have none
          because they typed a term that matches no filename. Same rule the project list states one
          panel up.
        */
        <p className="dochits__empty">No document name or type matches this search.</p>
      ) : (
        <>
          <ul className="dochits__list">
            {result.documents.map((d) => (
              <li className="dochits__row" key={`${d.projectRef}:${d.documentId}`}>
                {canRetrieve ? (
                  /* A real `href` so middle-click and "copy link address" do the obvious thing; the
                     handler takes over the ordinary click only to renew the session first. Identical
                     to the documents panel, deliberately — two ways to open a file that behave
                     differently is a way to open a file wrongly. */
                  <a
                    className="dochits__name"
                    href={documentDownloadUrl(d.projectRef, d.documentId)}
                    onClick={(e) => {
                      e.preventDefault();
                      onRetrieve(d.projectRef, d.documentId);
                    }}
                  >
                    {d.filename}
                  </a>
                ) : (
                  <span className="dochits__name dochits__name--plain">{d.filename}</span>
                )}
                <span className="dochits__meta">
                  {d.documentType ?? 'Unclassified'} · v{d.versionNumber} ·{' '}
                  <a
                    className="dochits__project"
                    href={`/projects/${encodeURIComponent(d.projectRef)}#documents`}
                  >
                    {d.projectName ?? d.projectCode ?? 'Unnumbered pursuit'}
                  </a>{' '}
                  · added {timestamp(d.submittedAt)}
                  {d.submittedByName ? ` by ${d.submittedByName}` : ''}
                </span>
                {/* canon R9's share verb. Offered to every reader, including a viewer: the link is
                    worth exactly the access its holder already has, so sharing it grants nothing. */}
                <CopyLinkButton
                  url={documentShareUrl(origin, d.projectRef, d.documentId)}
                  label={`Copy link to ${d.filename}`}
                />
                {d.withdrawnAt ? (
                  <span className="dochits__withdrawn">
                    Withdrawn {timestamp(d.withdrawnAt)}
                    {d.withdrawnByName ? ` by ${d.withdrawnByName}` : ''} — still the newest version
                    on the record.
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {result.truncated ? (
            /* ⚠️ THE CAP IS DISCLOSED. A truncated list that looks complete is worse than no list —
               a reader counts the rows and concludes that is all there is. */
            <p className="dochits__empty">
              Showing the {result.documents.length} most recent matches. Narrow the search to see
              others.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
